// src/components/scenarios/Scenarios.js

import { useEffect, useState } from 'react'
import { TITLE, X_LABEL, Y_LABEL, X_VALUES, Y_VALUES, LABEL_GENERATING_FUNCTION } from '../../templates/charts/ChartConstants'
import * as cc from '../../templates/charts/ChartConstants'
import { MultiLineChartHook } from '../../templates/charts/hooks/MultiLineChartHook'
import * as con from "../../GlobalConstants"
import { addDays, addMonths, daysBetweenDates } from '../../utils/dateFunctions'
import { useIsMobile } from '../hooks/ApplicationParametersHooks'
import { RefMarketValueHook, RefSPOT } from '../hooks/MarketValuesHook'
import { formatMoney } from '../../utils/moneyFunctions'
import { AllScenarioIds } from '../hooks/ScenariosHook'
import { selectTermInMonths } from '../../store/selectors/modelParametersSelector'
import { useSelector } from 'react-redux'
import { selectCurrentDate } from '../../store/selectors/applicationSelectors'
import { useMemo } from 'react'
import { makeMultipleSelectScenarioGeneratorsByIds } from '../../store/selectors/scenariosSlectors'

function Scenarios({ selectedScenarios = null, chartParameters = {} }) {

    // Parámetros del modelo
    const spot = RefSPOT()
    const termInMonths = useSelector(selectTermInMonths)
    const marketVolatility = RefMarketValueHook(con.MARKET_VOLATILITY)

    // Escenarios
    const scenarios = AllScenarioIds()
    const effectiveScenarioIds = selectedScenarios ?? scenarios;

    const selectScenarioGenerators = useMemo(
        () => makeMultipleSelectScenarioGeneratorsByIds(effectiveScenarioIds),
        [effectiveScenarioIds]
    )

    const scenarioGenerators = useSelector(selectScenarioGenerators)

    // Parámetros aplicación
    const historicPricesAndDates = RefMarketValueHook(con.HISTORIC_DOLLAR_INFO)
    const currentDate = useSelector(selectCurrentDate)
    const isMobile = useIsMobile()

    // Configuración del gráfico
    const chartParams = {
        [TITLE]: isMobile
            ? "ESCENARIOS"
            : "COMPARACIÓN ESCENARIOS EN LA TASA DE CAMBIO",
        [Y_LABEL]: isMobile ? "" : "Tasa de Cambio",
        [X_LABEL]: isMobile ? "" : "Fecha",
        
        ...chartParameters
    }

    if (isMobile) {
        chartParams.titleFontSize = '12px'
        chartParams.axisFontSize = '10px'
        chartParams.showLabels = false
        chartParams.showXAxisLabels = false
        chartParams[cc.INCLUDE_LEGEND] = false
    }

    const [chartData, setChartData] = useState([])
    const [objectReference, chart] = MultiLineChartHook({ parameters: chartParams })

    useEffect(() => {

        if (!chart.initialized) {
            chart.initialize()
        }

        if (!currentDate ||
            !historicPricesAndDates ||
            !scenarioGenerators ||
            scenarioGenerators.some(s => typeof s.gen_fun !== 'function'))
            return

        // =========================
        // Fechas proyectadas (Date reales)
        // =========================
        const finalDate = addMonths(currentDate, termInMonths)
        const numPoints = daysBetweenDates(currentDate, finalDate)

        const dates = [...Array(numPoints).keys()]
            .map(i => addDays(currentDate, i).toJSDate())

        // =========================
        // Histórico (normalizado a Date)
        // =========================
        const historicLength = historicPricesAndDates[con.DATES].length

        const historicDates =
            historicPricesAndDates[con.DATES]
                .slice(historicLength - con.HISTORIC_SCENARIO_DAYS_TO_DISPLAY)
                .map(d => {
                    if (d instanceof Date) return d
                    if (d?.toJSDate) return d.toJSDate()
                    return new Date(d)
                })
                .concat([currentDate.toJSDate()])

        const historicPrices =
            historicPricesAndDates[con.TRM]
                .slice(historicLength - con.HISTORIC_SCENARIO_DAYS_TO_DISPLAY)
                .concat([spot])

        // =========================
        // Construcción data
        // =========================
        let data = [{
            name: con.HISTORIC_PRICES_NAME,
            [X_VALUES]: historicDates,
            [Y_VALUES]: historicPrices,
            [LABEL_GENERATING_FUNCTION]: isMobile
                ? null
                : (selObj, j) =>
                    `${selObj[X_VALUES][j].toLocaleDateString()}: ${formatMoney(selObj[Y_VALUES][j], con.MONEY_COP)}`
        }]

        data = data.concat(
            scenarioGenerators
                .filter(({ id }) =>
                    selectedScenarios == null ||
                    selectedScenarios.includes(id)
                )
                .map(({ name, gen_fun }) => ({
                    name,
                    [X_VALUES]: dates,
                    [Y_VALUES]: dates.map((_, i) =>
                        gen_fun(spot, i)
                    ),
                    [LABEL_GENERATING_FUNCTION]: isMobile
                        ? null
                        : (selObj, j) =>
                            `${selObj[X_VALUES][j].toLocaleDateString()}: ${formatMoney(selObj[Y_VALUES][j], con.MONEY_COP)}`
                }))
        )

        // =========================
        // Build vs Update
        // =========================
        if (data.length !== chartData.length) {
            chart.build(data)
        } else {
            chart.updateData(data)
            chart.updateLegend(data)
        }

        setChartData(data)

    }, [
        historicPricesAndDates,
        currentDate,
        spot,
        termInMonths,
        marketVolatility,
        scenarioGenerators,
        selectedScenarios,
        isMobile,
        chart,
        chartData.length
    ])

    return (
        <svg ref={objectReference} />
    )
}

export default Scenarios